import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { X, Users, Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

interface CoParentInviteModalProps {
  studentId: string;
  studentFirstName: string;
  onClose: () => void;
}

interface CoParentEntry {
  first_name: string;
  last_name: string;
  email: string;
  relationship: string;
  is_primary: boolean;
}

type Phase =
  | { tag: 'idle' }
  | { tag: 'searching' }
  | { tag: 'preview'; found: true; firstName: string; lastName: string }
  | { tag: 'preview'; found: false }
  | { tag: 'sending' }
  | { tag: 'done'; resultType: 'access_granted' | 'invite_sent'; inviteeEmail: string }
  | { tag: 'error'; message: string; code?: string };

interface FindParentResult {
  found: boolean;
  first_name?: string;
  last_name?: string;
}

interface InviteResult {
  type: 'access_granted' | 'invite_sent';
}

interface FnError {
  context?: {
    json?: () => Promise<{ error?: string; message?: string }>;
  };
  message?: string;
}

async function parseInviteError(err: unknown): Promise<{ message: string; code?: string }> {
  const fnErr = err as FnError;
  if (fnErr?.context?.json) {
    try {
      const body = await fnErr.context.json();
      return { message: body.message ?? 'Something went wrong', code: body.error };
    } catch {
      // fall through
    }
  }
  return { message: err instanceof Error ? err.message : 'Something went wrong' };
}

export function CoParentInviteModal({ studentId, studentFirstName, onClose }: CoParentInviteModalProps) {
  const [view, setView] = useState<'roster' | 'invite'>('roster');
  const [coParents, setCoParents] = useState<CoParentEntry[] | null>(null);
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<Phase>({ tag: 'idle' });

  useEffect(() => {
    supabase.rpc('get_co_parents_for_student', { p_student_id: studentId }).then(({ data, error }) => {
      if (error) {
        setCoParents([]);
        return;
      }
      setCoParents((data as CoParentEntry[]) ?? []);
    });
  }, [studentId]);

  const normalizedEmail = email.trim().toLowerCase();
  const emailValid = normalizedEmail.includes('@') && normalizedEmail.includes('.');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailValid) return;
    setPhase({ tag: 'searching' });
    try {
      const { data, error } = await supabase.rpc('find_parent_by_email', { p_email: normalizedEmail });
      if (error) throw error;
      const result = data as FindParentResult | null;
      if (result?.found) {
        setPhase({ tag: 'preview', found: true, firstName: result.first_name ?? '', lastName: result.last_name ?? '' });
      } else {
        setPhase({ tag: 'preview', found: false });
      }
    } catch {
      setPhase({ tag: 'error', message: 'Failed to look up email. Please try again.' });
    }
  };

  const handleSend = async () => {
    setPhase({ tag: 'sending' });
    try {
      const { data, error } = await supabase.functions.invoke('invite-co-parent', {
        body: { student_id: studentId, invitee_email: normalizedEmail },
      });
      if (error) {
        const parsed = await parseInviteError(error);
        setPhase({ tag: 'error', message: parsed.message, code: parsed.code });
        return;
      }
      const result = data as InviteResult;
      setPhase({ tag: 'done', resultType: result.type, inviteeEmail: normalizedEmail });
    } catch (err) {
      const parsed = await parseInviteError(err);
      setPhase({ tag: 'error', message: parsed.message, code: parsed.code });
    }
  };

  const handleReset = () => {
    setEmail('');
    setPhase({ tag: 'idle' });
  };

  const isSearching = phase.tag === 'searching';
  const isSending = phase.tag === 'sending';
  const isBusy = isSearching || isSending;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end sm:items-center sm:justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-gray-500/75" onClick={onClose} aria-hidden="true" />
      <div
        className="relative w-full bg-white rounded-t-2xl shadow-xl sm:rounded-lg sm:max-w-md sm:my-8 flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden" aria-hidden="true">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-600" />
            <h2 className="text-base font-semibold text-gray-900">
              Invite a co-parent — {studentFirstName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto p-5" style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom, 0px))" }}>

        {/* Roster view — shown on open */}
        {view === 'roster' && (
          <>
            {coParents === null ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
              </div>
            ) : coParents.length === 0 ? (
              <p className="text-sm text-gray-500 py-2 mb-4">No co-parents yet.</p>
            ) : (
              <>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Currently has access
                </p>
                <ul className="space-y-2 mb-4">
                  {coParents.map((cp, i) => (
                    <li key={i} className="flex items-center justify-between text-sm py-2 px-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="font-medium text-gray-800">
                          {cp.first_name} {cp.last_name}
                        </span>
                        <span className="block text-xs text-gray-500">{cp.email}</span>
                      </div>
                      <span className="text-xs text-gray-400 capitalize ml-4 shrink-0">{cp.relationship}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => setView('invite')}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Invite a co-parent
              </button>
            </div>
          </>
        )}

        {/* Invite view — email input flow */}
        {view === 'invite' && (
          <>
            {/* Done state */}
            {phase.tag === 'done' && (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                {phase.resultType === 'access_granted' ? (
                  <>
                    <p className="text-base font-semibold text-gray-900 mb-1">Access granted!</p>
                    <p className="text-sm text-gray-600">
                      <strong>{phase.inviteeEmail}</strong> can now view and manage {studentFirstName}'s enrollment and payments.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-base font-semibold text-gray-900 mb-1">Invite sent!</p>
                    <p className="text-sm text-gray-600">
                      An invitation was emailed to <strong>{phase.inviteeEmail}</strong>. As soon as they sign in with that email — now or later — they'll automatically get access to {studentFirstName}.
                    </p>
                  </>
                )}
                <button
                  onClick={onClose}
                  className="mt-5 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  Done
                </button>
              </div>
            )}

            {/* Error state */}
            {phase.tag === 'error' && (
              <div className="text-center py-4">
                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <p className="text-base font-semibold text-gray-900 mb-1">
                  {phase.code === 'ALREADY_CO_PARENT' ? 'Already a co-parent' : 'Something went wrong'}
                </p>
                <p className="text-sm text-gray-600 mb-5">{phase.message}</p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={handleReset}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  >
                    Try again
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Input + preview states */}
            {phase.tag !== 'done' && phase.tag !== 'error' && (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Enter the email address of the person you'd like to share access with.
                </p>

                <form onSubmit={handleSearch}>
                  <div className="flex gap-2 mb-4">
                    <div className="flex-1 relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <input
                        type="email"
                        value={email}
                        onChange={e => { setEmail(e.target.value); if (phase.tag === 'preview') setPhase({ tag: 'idle' }); }}
                        placeholder="co-parent@example.com"
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isBusy}
                        autoComplete="off"
                        autoFocus
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={!emailValid || isBusy}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSearching ? 'Checking…' : 'Check'}
                    </button>
                  </div>
                </form>

                {/* Preview: found */}
                {phase.tag === 'preview' && phase.found && (
                  <div className="border border-green-200 bg-green-50 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-green-800 mb-1">Existing BHCS account found</p>
                    <p className="text-sm text-green-700">
                      <strong>{phase.firstName} {phase.lastName}</strong> already has a BHCS account.
                      Confirming will grant them immediate access to {studentFirstName}'s enrollment and payments.
                    </p>
                  </div>
                )}

                {/* Preview: not found */}
                {phase.tag === 'preview' && !phase.found && (
                  <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-blue-800 mb-1">No BHCS account yet</p>
                    <p className="text-sm text-blue-700">
                      We'll email an invite to <strong>{normalizedEmail}</strong>. Whenever they sign in with that
                      email — by tapping the link or just signing up later — they'll automatically be connected to {studentFirstName}.
                    </p>
                  </div>
                )}

                {/* Confirm button (shown after preview) */}
                {phase.tag === 'preview' && (
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={handleReset}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSend}
                      disabled={isSending}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isSending
                        ? 'Sending…'
                        : phase.found
                          ? 'Grant access'
                          : 'Send invite'}
                    </button>
                  </div>
                )}

                {/* Back link */}
                {phase.tag === 'idle' && (
                  <div className="mt-1">
                    <button
                      onClick={() => setView('roster')}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      ← Back
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
        </div>{/* overflow-y-auto body */}
      </div>
    </div>
  );
}
